/**
 * CTO Platform API Service
 * Handles: Onboarding, Registration Check, Document Upload, Admin Approval
 * Separate from CTODOCX api.js — different API Gateway, same Microsoft SSO token.
 */

import { msalConfig } from "../authConfig";
import { PublicClientApplication } from "@azure/msal-browser";

const PLATFORM_API_BASE = process.env.REACT_APP_PLATFORM_API_BASE_URL || "";
let msalInstance = null;

export function initPlatformApi(instance) {
  msalInstance = instance;
}

async function getToken() {
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(msalConfig);
    await msalInstance.initialize();
  }
  const account = msalInstance.getActiveAccount();
  if (!account) {
    // No account — session gone, send back to login
    msalInstance.loginRedirect({ scopes: ["openid", "profile", "email"] });
    throw new Error("Session expired. Redirecting to login...");
  }

  try {
    const resp = await msalInstance.acquireTokenSilent({
      scopes: ["openid", "profile", "email"],
      account,
    });
    return resp.idToken;
  } catch (silentErr) {
    try {
      const resp = await msalInstance.acquireTokenPopup({
        scopes: ["openid", "profile", "email"],
        account,
      });
      return resp.idToken;
    } catch (popupErr) {
      // Both failed — redirect to login instead of surfacing a cryptic error
      msalInstance.loginRedirect({ scopes: ["openid", "profile", "email"] });
      throw new Error("Session expired. Please sign in again.");
    }
  }
}

async function apiCall(method, path, body = null) {
  const token = await getToken();
  const options = {
    method,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
  };
  if (body) options.body = JSON.stringify(body);

  const url = `${PLATFORM_API_BASE}${path}`;
  const res = await fetch(url, options);
  const text = await res.text();

  let data;
  try {
    data = JSON.parse(text);
  } catch {
    data = { message: text };
  }

  if (!res.ok) {
    throw new Error(data.error || data.message || `API Error: ${res.status}`);
  }

  return data;
}

// ═══════════════════════════════════════════════════════
// Registration Check
// ═══════════════════════════════════════════════════════

export async function checkRegistration() {
  return apiCall("GET", "/user/check-registration");
}

// ═══════════════════════════════════════════════════════
// Document Upload (Presigned URL)
// ═══════════════════════════════════════════════════════

export async function getPresignedUploadUrl(docType, contentType, fileSize) {
  // Backend returns { uploadUrl, s3Key } (camelCase)
  return apiCall("POST", "/upload/presigned", { docType, contentType, fileSize });
}

export async function uploadFileToS3(presignedUrl, file, contentType) {
  const res = await fetch(presignedUrl, {
    method: "PUT",
    headers: { "Content-Type": contentType },
    body: file,
  });
  if (!res.ok) throw new Error("File upload failed");
  return true;
}

// ═══════════════════════════════════════════════════════
// Onboarding Submission
// ═══════════════════════════════════════════════════════

export async function submitOnboarding(data) {
  return apiCall("POST", "/onboarding", data);
}

// ═══════════════════════════════════════════════════════
// Admin: Onboarding Requests
// ═══════════════════════════════════════════════════════

export async function getOnboardingRequests() {
  return apiCall("GET", "/admin/onboarding-requests");
}

export async function getOnboardingDetail(requestId) {
  return apiCall("GET", `/admin/onboarding-requests/${requestId}`);
}

export async function approveOnboarding(requestId, body) {
  return apiCall("PUT", `/admin/onboarding-requests/${requestId}/approve`, body);
}



// =========================================================
// FRONTEND — add this ONE function to src/services/platformApi.js
// (put it near checkRegistration; the apiCall helper already exists)
// =========================================================

export async function getMyProfile() {
  // Backend returns { success: true, data: {...profile...} }
  return apiCall("GET", "/user/profile");
}



// =========================================================
// STEP 4 — add these to src/services/platformApi.js
// (getPresignedUploadUrl + uploadFileToS3 already exist — reused for doc re-upload)
// =========================================================

// Employee submits an edit request (text changes + optional new document keys)
export async function submitChangeRequest(payload) {
  // payload = { changes: {...}, documents: {...}, reason: "" }
  return apiCall("POST", "/user/profile/change-request", payload);
}

// Admin: list pending change requests
export async function getChangeRequests() {
  return apiCall("GET", "/admin/change-requests");
}

// Admin: approve or reject a change request
export async function reviewChangeRequest(requestId, decision, note = "") {
  // decision = "APPROVE" | "REJECT"
  return apiCall("PUT", `/admin/change-requests/${requestId}/review`, { decision, note });
}



// Admin: list all employees (active/inactive)
export async function getAllUsers() {
  return apiCall("GET", "/admin/users");
}

// Admin: full detail of one employee
export async function getUserDetail(userId) {
  return apiCall("GET", `/admin/users/${userId}`);
}

// Admin: update dates / role / status (all-in-one)
// body example: { dateOfJoining, relievingDate, role, status }
export async function updateUser(userId, body) {
  return apiCall("PUT", `/admin/users/${userId}`, body);
}
